/**
 * The pages `client_navigate` can send a user to.
 *
 * Separate from `navigate.ts` so the approval card can read the registry:
 * `navigate.ts` imports the card, so the card importing `navigate.ts` back
 * would be a runtime cycle.
 *
 * A new entry here reaches the tool description, the route enum in the tool's
 * JSON Schema, and the approval card on its own. A new *param name* does not —
 * it must also be added to `NavigateParams` and to the `params` schema in
 * `navigate.ts`, which rejects properties it does not declare.
 */
import { Goto } from "@/shared/navigation";

/**
 * A parameter a navigable route accepts. Two audiences read it: `label` names
 * the field in the approval card ("Dataset"), and `description` goes into the
 * tool description the model reads.
 */
export interface NavigableRouteParam {
  name: string;
  label: string;
  description: string;
  required?: boolean;
  enum?: string[];
}

/** Fields every route carries, fixed or parameterized. */
interface NavigableRouteBase {
  name: string;
  /**
   * The destination as a noun phrase, read as "Go to {label}" in the approval
   * card. Spelled out rather than derived from `name`, which would put
   * "Go to settings organization" in front of a user.
   */
  label: string;
  /** What the page is for, read by the model when it picks a route. */
  description: string;
}

/** A route with no parameters; the tool navigates straight to `path`. */
export interface FixedRoute extends NavigableRouteBase {
  path: string;
}

/** A route whose destination depends on model-supplied parameters. */
export interface ParameterizedRoute extends NavigableRouteBase {
  params: NavigableRouteParam[];
  /**
   * Navigate, and return the path to report back to the model. Throws when a
   * required param is missing, which the tool returns as an error result.
   */
  execute: (goto: Goto, params: Record<string, string | undefined>) => string;
}

export type NavigableRoute = FixedRoute | ParameterizedRoute;

export function isParameterized(
  route: NavigableRoute,
): route is ParameterizedRoute {
  return "params" in route;
}

/**
 * The param's value. Throws when the model omits it, and `client_navigate`
 * returns that message as the tool's error result. `required: true` in the
 * registry documents the requirement; nothing enforces it before `execute`
 * runs.
 */
function requireParam(
  params: Record<string, string | undefined>,
  name: string,
  routeName: string,
): string {
  const value = params[name];
  if (!value) {
    throw new Error(`${name} is required for the ${routeName} route`);
  }
  return value;
}

/** Every route `client_navigate` can target. */
export const NAVIGABLE_ROUTES: readonly NavigableRoute[] = [
  // -- Fixed routes --
  {
    name: "home",
    label: "the dashboard",
    description: "Dashboard / home page",
    path: Goto.route.Home,
  },
  {
    name: "search",
    label: "search",
    description: "Search page for datasets, files, and topics",
    path: Goto.route.Search,
  },
  {
    name: "datasets",
    label: "the datasets list",
    description: "List of all datasets in the organization",
    path: Goto.route.Datasets,
  },
  {
    name: "create_dataset",
    label: "the new dataset form",
    description: "Form to create a new dataset",
    path: Goto.route.CreateDataset,
  },
  {
    name: "collections",
    label: "the collections list",
    description: "List of all collections in the organization",
    path: Goto.route.Collections,
  },
  {
    name: "actions",
    label: "the actions list",
    description: "List of all actions (data processing pipelines)",
    path: Goto.route.Actions,
  },
  {
    name: "action_hub",
    label: "the action hub",
    description: "Marketplace of shared and community actions",
    path: Goto.route.ActionHub,
  },
  {
    name: "create_action",
    label: "the new action form",
    description: "Form to create a new action",
    path: Goto.route.CreateAction,
  },
  {
    name: "create_trigger",
    label: "the new trigger form",
    description: "Form to create a new trigger (automated action execution)",
    path: Goto.route.CreateTrigger,
  },
  {
    name: "devices",
    label: "the devices list",
    description: "List of all registered devices (robots, sensors, etc.)",
    path: Goto.route.Devices,
  },
  {
    name: "account",
    label: "account settings",
    description: "User account settings and profile",
    path: Goto.route.Account,
  },
  {
    name: "settings_organization",
    label: "organization settings",
    description: "Organization settings (name, members, billing)",
    path: Goto.route.Settings.Organization,
  },
  {
    name: "settings_tokens",
    label: "API token settings",
    description: "API token management",
    path: Goto.route.Settings.Tokens,
  },
  {
    name: "settings_usage",
    label: "the usage dashboard",
    description: "Usage dashboard showing API calls and storage",
    path: Goto.route.Settings.Usage,
  },
  {
    name: "settings_ai",
    label: "AI settings",
    description: "AI assistant settings and configuration",
    path: Goto.route.Settings.AI,
  },
  {
    name: "settings_secrets",
    label: "secrets settings",
    description: "Secrets management for action environment variables",
    path: Goto.route.Settings.Secrets,
  },
  {
    name: "agents_skills",
    label: "the skills page",
    description: "Manage stored skills — versioned procedures the AI can apply",
    path: Goto.route.AgentSkills,
  },

  // -- Parameterized routes --
  {
    name: "dataset",
    label: "a dataset",
    description:
      "View a specific dataset including its files, topics, events, and details",
    params: [
      {
        name: "datasetId",
        label: "Dataset",
        description: "The dataset ID (e.g. 'ds_abc123')",
        required: true,
      },
      {
        name: "tab",
        label: "Tab",
        description: "Which tab to open on the dataset page",
        enum: [
          "files",
          "topics",
          "events",
          "actions",
          "details",
          "permissions",
          "summary",
        ],
      },
    ],
    execute: (goto, params) => {
      const datasetId = requireParam(params, "datasetId", "dataset");
      const basePath = Goto.createDatasetRoute(datasetId);
      switch (params.tab) {
        case "files":
          // The dataset index route redirects to the files tab.
          goto.dataset({ datasetId });
          return `${basePath}/files`;
        case "topics":
          goto.datasetTopics({ datasetId });
          return `${basePath}/topics`;
        case "events":
          goto.datasetEvents({ datasetId });
          return `${basePath}/events`;
        case "actions":
          goto.datasetActions({ datasetId });
          return `${basePath}/actions`;
        case "details":
          goto.datasetDetails({ datasetId });
          return `${basePath}/details`;
        case "permissions":
          goto.datasetPermissions({ datasetId });
          return `${basePath}/permissions`;
        case "summary": {
          const summaryPath = `${basePath}/summary`;
          goto.navigateTo(summaryPath);
          return summaryPath;
        }
        default:
          // `tab` carries no enum in the tool's JSON Schema; an omitted or
          // unrecognized value lands on the default tab rather than erroring.
          goto.dataset({ datasetId });
          return basePath;
      }
    },
  },
  {
    name: "collection",
    label: "a collection",
    description: "View a specific collection of datasets",
    params: [
      {
        name: "collectionId",
        label: "Collection",
        description: "The collection ID",
        required: true,
      },
    ],
    execute: (goto, params) => {
      const collectionId = requireParam(params, "collectionId", "collection");
      goto.collection({ collectionId });
      return Goto.createCollectionRoute(collectionId);
    },
  },
  {
    name: "action",
    label: "an action",
    description: "View a specific action's details and configuration",
    params: [
      {
        name: "actionName",
        label: "Action",
        description: "The action name",
        required: true,
      },
      {
        name: "actionOwner",
        label: "Owner",
        description:
          "The action owner (org or user). Omit for actions owned by the current org.",
      },
    ],
    execute: (goto, params) => {
      const action = {
        name: requireParam(params, "actionName", "action"),
        owner: params.actionOwner,
      };
      goto.action({ action });
      return Goto.createActionRoute(action);
    },
  },
  {
    name: "invoke_action",
    label: "the action invocation form",
    description: "Open the action invocation form, optionally pre-filled",
    params: [
      {
        name: "actionName",
        label: "Action",
        description: "Action name to pre-select",
      },
      {
        name: "actionOwner",
        label: "Owner",
        description: "Action owner to pre-select",
      },
      {
        name: "datasetId",
        label: "Dataset",
        description: "Dataset ID to pre-select as input",
      },
    ],
    execute: (goto, params) => {
      const action = params.actionName
        ? { name: params.actionName, owner: params.actionOwner }
        : undefined;
      goto.invokeAction({ action, datasetId: params.datasetId });
      return Goto.route.InvokeAction;
    },
  },
  {
    name: "invocation",
    label: "an invocation",
    description: "View the results and logs of a specific action invocation",
    params: [
      {
        name: "invocationId",
        label: "Invocation",
        description: "The invocation ID",
        required: true,
      },
    ],
    execute: (goto, params) => {
      const invocationId = requireParam(params, "invocationId", "invocation");
      goto.invocation({ invocationId });
      return Goto.createInvocationRoute(invocationId);
    },
  },
  {
    name: "device",
    label: "a device",
    description:
      "View a specific device's details, tokens, datasets, and files",
    params: [
      {
        name: "deviceId",
        label: "Device",
        description: "The device ID",
        required: true,
      },
    ],
    execute: (goto, params) => {
      const deviceId = requireParam(params, "deviceId", "device");
      goto.device({ deviceId });
      return Goto.createDeviceRoute(deviceId);
    },
  },
  {
    name: "file",
    label: "a file",
    description:
      "View a specific file in the file viewer (supports images, point clouds, time-series, etc.)",
    params: [
      {
        name: "fileId",
        label: "File",
        description: "The file ID",
        required: true,
      },
    ],
    execute: (goto, params) => {
      const fileId = requireParam(params, "fileId", "file");
      // goto.file() scrolls the window to the top; that jump is unwanted for
      // navigation the user did not click.
      const filePageRoute = Goto.createFileRoute(fileId);
      goto.navigateTo(filePageRoute);
      return filePageRoute;
    },
  },
  {
    name: "trigger",
    label: "a trigger",
    description: "View a specific trigger's configuration and history",
    params: [
      {
        name: "triggerId",
        label: "Trigger",
        description: "The trigger ID",
        required: true,
      },
    ],
    execute: (goto, params) => {
      // The trigger page also accepts a `tr_` id where the name goes and
      // redirects to the name's URL, so either value opens the trigger.
      const triggerId = requireParam(params, "triggerId", "trigger");
      // A trigger's pages open by id; a name can belong to a deleted trigger
      // and then to a new one, so it is never resolved.
      if (!triggerId.startsWith("tr_")) {
        throw new Error(
          `The trigger route needs a trigger ID (e.g. 'tr_r3p7xo59447r'), not a name; '${triggerId}' is not a trigger ID`,
        );
      }
      const triggerPath = Goto.createTriggerRoute(triggerId);
      goto.navigateTo(triggerPath);
      return triggerPath;
    },
  },
  {
    name: "workspace",
    label: "a visualizer workspace",
    description: "Open a saved visualizer workspace",
    params: [
      {
        name: "workspaceId",
        label: "Workspace",
        description:
          "The visualizer workspace ID (e.g. 'ws_abc123'), as returned by create_visualizer_workspace",
        required: true,
      },
    ],
    execute: (goto, params) => {
      const workspaceId = requireParam(params, "workspaceId", "workspace");
      // `goto.visualize()` accepts datasources as search params, not a saved
      // workspace ID.
      const workspacePath = Goto.createVisualizerRoute(workspaceId);
      goto.navigateTo(workspacePath);
      return workspacePath;
    },
  },
];

/** The registered route with this name, or `undefined` if there is none. */
export function findNavigableRoute(
  routeName: string,
): NavigableRoute | undefined {
  return NAVIGABLE_ROUTES.find((route) => route.name === routeName);
}

/** The route catalog embedded in the `client_navigate` tool description. */
export function buildRouteDescriptions(): string {
  return NAVIGABLE_ROUTES.map((route) => {
    let line = `- "${route.name}": ${route.description}`;
    if (isParameterized(route)) {
      const paramDescs = route.params.map((param) => {
        const req = param.required ? "required" : "optional";
        const enumNote = param.enum
          ? ` (one of: ${param.enum.join(", ")})`
          : "";
        return `${param.name} (${req}${enumNote}): ${param.description}`;
      });
      line += `\n  Params: ${paramDescs.join("; ")}`;
    }
    return line;
  }).join("\n");
}
